import { useState, useCallback } from "react";
import type { ConversionResult } from "../api/types";

const RECENT_KEY = "recent_conversions";
const MAX_ITEMS = 5;
// Don't persist the markdown of very large results (e.g. image-heavy PDFs whose
// embedded base64 images bloat the text). localStorage is only ~5 MB total, so a
// single big result would exceed the quota. Such items still appear in the list,
// they just can't be restored from history.
const MAX_PERSIST_MARKDOWN_CHARS = 1_500_000;

export interface RecentItem {
  filename: string;
  wordCount: number;
  pageCount: number;
  imagesFound: number;
  durationMs: number;
  timestamp: number;
  markdown: string;
  /** False when the markdown was too large to persist — list-only, not restorable. */
  restorable: boolean;
}

function load(): RecentItem[] {
  try {
    const items = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    if (!Array.isArray(items)) return [];
    // Backfill `restorable` for items saved before the field existed.
    return items.map((i) => ({ ...i, restorable: i.restorable ?? Boolean(i.markdown) }));
  } catch {
    return [];
  }
}

/**
 * Persist items, surviving localStorage quota limits. Returns the list that was
 * actually stored — on a quota error we progressively drop the oldest items and
 * retry, so a single oversized result can never throw and crash the app.
 */
function save(items: RecentItem[]): RecentItem[] {
  let candidate = items;
  while (candidate.length > 0) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(candidate));
      return candidate;
    } catch {
      candidate = candidate.slice(0, -1); // drop the oldest entry and retry
    }
  }
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    /* ignore */
  }
  return [];
}

export function useRecentConversions() {
  const [items, setItems] = useState<RecentItem[]>(load);

  const add = useCallback((result: ConversionResult) => {
    setItems((prev) => {
      const tooLarge = result.markdown.length > MAX_PERSIST_MARKDOWN_CHARS;
      const next: RecentItem = {
        filename: result.filename,
        wordCount: result.word_count,
        pageCount: result.page_count,
        imagesFound: result.images_found,
        durationMs: result.duration_ms,
        timestamp: Date.now(),
        markdown: tooLarge ? "" : result.markdown,
        restorable: !tooLarge,
      };
      const filtered = prev.filter((i) => i.filename !== result.filename);
      const updated = [next, ...filtered].slice(0, MAX_ITEMS);
      return save(updated);
    });
  }, []);

  const remove = useCallback((timestamp: number) => {
    // Route through save() so React state always matches what's in storage.
    setItems((prev) => save(prev.filter((i) => i.timestamp !== timestamp)));
  }, []);

  return { items, add, remove };
}
